import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { AppContext } from "../../../context/AppContext";
import "../Announcement/Announcement.scss";
import api from "../../../api.js";
import { io } from "socket.io-client";
import { Calendar } from "primereact/calendar";
import Loader from "../../../components/Loader/Loader.jsx";

const VisitorsPage = () => {
  const { backendUrl } = useContext(AppContext);
  const [allVisitorsGrievances, setAllVisitorsGrievances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [scrollHeight, setScrollHeight] = useState("425px");
  let errorShown = false;

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerHeight;
      if (screenWidth >= 900) {
        setScrollHeight("650px");
      } else {
        setScrollHeight("440px");
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  async function fetchAllVisitorsGrievances() {
    setLoading(true);
    try {
      const { data } = await api.get(
        `${backendUrl}/api/auth/getAllVisitorsGrievances`
      );
      setAllVisitorsGrievances(data.visitorsGrievances);
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const newSocket = io(backendUrl, {
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      transports: ["websocket"],
    });
    setSocket(newSocket);
    return () => {
      newSocket.disconnect();
    };
  }, [backendUrl]);

  useEffect(() => {
    if (!socket) return;
    fetchAllVisitorsGrievances();
    socket.on("update_helpdesk_visitors", (newGrievance) => {
      setAllVisitorsGrievances((prev) => {
        if (!prev.some((g) => g.id === newGrievance.id)) {
          return [newGrievance, ...prev];
        }
        return prev;
      });
      toast.info("📩 New visitor grievance received!");
    });
    return () => {
      socket.off("update_helpdesk_visitors");
      socket.disconnect();
    };
  }, [socket]);

  const showConfirmDialog = (rowData) => {
    confirmDialog({
      message: (
        <div className="custom-dialog-content">
          <div className="iconParent">
            <i className="pi pi-times"></i>
          </div>
          <div className="contentParent">
            <h2>Are you sure?</h2>
            <div className="contentSubParent">
              <p>Delete grievance from {rowData.visitorName}</p>
              <p>Grievance: {rowData.grievance.substring(0, 50)}...</p>
              <div>
                <p className="warningText">This action cannot be undone.</p>
              </div>
            </div>
          </div>
        </div>
      ),
      header: "",
      icon: null,
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger custom-delete-btn",
      rejectClassName: "p-button-secondary custom-cancel-btn",
      accept: () => {
        handleDeleteClick(rowData);
      },
    });
  };

  async function handleDeleteClick(rowData) {
    setIsSubmitting(true);
    try {
      const { data } = await api.delete(
        `${backendUrl}/api/auth/deleteVisitorGrievance/${rowData.id}`
      );
      if (data.success) {
        toast.success(data.message);
        fetchAllVisitorsGrievances();
      }
    } catch (error) {
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const formatToDDMMYYYY = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleDownloadVisitorHelpDesk = async () => {
    setIsSubmitting(true);
    if (!startDate || !endDate) {
      toast.warn("Please select both start and end dates.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start date cannot be after end date.");
      return;
    }
    try {
      setLoading(true);
      const formattedStart = formatToDDMMYYYY(startDate);
      const formattedEnd = formatToDDMMYYYY(endDate);
      const response = await api.get(
        `${backendUrl}/api/auth/exportVisitorHelpDeskExcel`,
        {
          params: {
            startDate: formattedStart,
            endDate: formattedEnd,
          },
          responseType: "blob",
        }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Visitor_HelpDesk_${formattedStart}_to_${formattedEnd}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download Excel file.");
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <section className="adminAnnoucementMainContainer">
      <Loader visible={isSubmitting} />

      <div className="headerParent">
        <h2>Welcome to Admin HelpDesk Visitors Page</h2>
      </div>

      <div className="dateAndDownloadBtnContainer">
        <div className="dateFieldParent">
          <label htmlFor="startDate" className="dateLabelNames">
            Start Date
          </label>
          <Calendar
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.value)}
            dateFormat="dd-mm-yy"
            showIcon
            inputStyle={{ padding: "0.6rem 0.8rem", width: "175px" }}
          />
        </div>
        <div className="dateFieldParent">
          <label htmlFor="endDate" className="dateLabelNames">
            End Date
          </label>
          <Calendar
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.value)}
            dateFormat="dd-mm-yy"
            showIcon
            inputStyle={{ padding: "0.6rem 0.8rem", width: "175px" }}
          />
        </div>
        <div className="downloadExcelBtnParent">
          <Button
            label=" Download Excel"
            icon="pi pi-download"
            className="p-button-success"
            onClick={handleDownloadVisitorHelpDesk}
            disabled={loading}
            style={{
              padding: "0.6rem 1.5rem",
              fontWeight: "600",
              fontSize: "1rem",
              fontFamily: "var(--font-paragraph)",
              gap: "0.5rem",
            }}
          />
        </div>
      </div>

      <article className="tableWrapper">
        <div className="scrollableTableContainer">
          <DataTable
            value={allVisitorsGrievances}
            showGridlines
            stripedRows
            className="table"
            emptyMessage="No grievance available"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
            scrollable
            scrollHeight={scrollHeight}
            loading={loading}
          >
            <Column
              header="S.No"
              body={(_, options) => options.rowIndex + 1}
              className="tableData"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
            />
            <Column
              field="visitorName"
              header="Visitor Name"
              className="tableData"
              style={{ minWidth: "210px", maxWidth: "325px" }}
            />
            <Column
              field="residentialAddress"
              header="Residential Address"
              className="tableData"
              style={{ minWidth: "250px", maxWidth: "325px" }}
            />
            <Column
              field="contactNumber"
              header="Visitor - Contact"
              className="tableData"
              style={{ minWidth: "210px", maxWidth: "325px" }}
            />
            <Column
              field="email"
              header="Mail ID"
              className="tableData"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
            />
            <Column
              field="grievance"
              header="Grievance"
              className="tableData"
              style={{ minWidth: "275px", maxWidth: "325px" }}
            />
            <Column
              field="idProof"
              header="Id Proof"
              className="tableData"
              style={{ minWidth: "325px", maxWidth: "325px" }}
              body={(rowData) => {
                const fileName = rowData.idProof_filename;
                const fileType = rowData.idProof_contentType;
                const data = rowData.idProof_data;
                if (!fileName || !fileType || !data) {
                  return <span>No Resume Proof</span>;
                }
                const byteCharacters = atob(data);
                const byteNumbers = Array.from(byteCharacters).map((char) =>
                  char.charCodeAt(0)
                );
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: fileType });
                const downloadUrl = URL.createObjectURL(blob);
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>{fileName}</span>
                    <a
                      href={downloadUrl}
                      download={fileName}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-pr-tooltip="Download file"
                      data-pr-position="top"
                      style={{ marginLeft: "10px" }}
                    >
                      <Button
                        icon="pi pi-download"
                        rounded
                        text
                        severity="info"
                        aria-label="Download"
                        className="download-button"
                      />
                    </a>
                  </div>
                );
              }}
            />
            <Column
              field="grievanceProof"
              header="Grievance Proof"
              style={{ minWidth: "325px", maxWidth: "325px" }}
              className="tableData"
              body={(rowData) => {
                const fileName = rowData.grievanceProof_filename;
                const fileType = rowData.grievanceProof_contentType;
                const data = rowData.grievanceProof_data;
                if (!fileName || !fileType || !data) {
                  return <span>No Resume Proof</span>;
                }
                const byteCharacters = atob(data);
                const byteNumbers = Array.from(byteCharacters).map((char) =>
                  char.charCodeAt(0)
                );
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: fileType });
                const downloadUrl = URL.createObjectURL(blob);
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>{fileName}</span>
                    <a
                      href={downloadUrl}
                      download={fileName}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-pr-tooltip="Download file"
                      data-pr-position="top"
                      style={{ marginLeft: "10px" }}
                    >
                      <Button
                        icon="pi pi-download"
                        rounded
                        text
                        severity="info"
                        aria-label="Download"
                        className="download-button"
                      />
                    </a>
                  </div>
                );
              }}
            />
            <Column
              field="createdAt"
              header="Created At"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
              className="tableData"
            />
            <Column
              header="Action"
              style={{ width: "1%", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap", maxWidth: "fit-content" }}
              body={(rowData) => (
                <div className="action-buttons">
                  <Button
                    label="Delete"
                    severity="danger"
                    className="actionBtns"
                    onClick={() => showConfirmDialog(rowData)}
                  />
                </div>
              )}
              className="tableData"
              frozen
              alignFrozen="right"
            />
          </DataTable>
        </div>
      </article>
    </section>
  );
};

export default VisitorsPage;
